import {
  clearPendingCaseDeletions,
  clearPendingCaseWipe,
  loadPendingCaseDeletions,
  loadPendingCaseWipe,
  normalizeCases
} from "../storage";
import type { RealEstateCase } from "../types";
import { supabaseRequest, type SupabaseSession } from "./supabase";

export type SyncStatus = "Synced" | "Syncing" | "Offline";

type CaseRow = {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  data: unknown;
};

export type CaseSyncResult = {
  cases: RealEstateCase[];
  session: SupabaseSession;
};

const timestamp = (value: string | null | undefined) => {
  const parsed = value ? Date.parse(value) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeCloudCase = (row: CaseRow): RealEstateCase | null => {
  if (typeof row.data !== "object" || row.data === null || Array.isArray(row.data)) {
    return null;
  }

  const data = row.data as Record<string, unknown>;
  return normalizeCases([
    {
      ...data,
      id: row.id,
      createdAt: typeof data.createdAt === "string" ? data.createdAt : row.created_at,
      updatedAt: typeof data.updatedAt === "string" ? data.updatedAt : row.updated_at
    }
  ])[0] ?? null;
};

const upsertRows = async (
  rows: Array<Record<string, unknown>>,
  session: SupabaseSession
): Promise<SupabaseSession> => {
  if (rows.length === 0) {
    return session;
  }

  const result = await supabaseRequest<void>("/rest/v1/cases?on_conflict=user_id%2Cid", session, {
    method: "POST",
    headers: {
      Prefer: "resolution=merge-duplicates,return=minimal"
    },
    body: JSON.stringify(rows)
  });
  return result.session;
};

const fetchCloudRows = async (session: SupabaseSession) =>
  supabaseRequest<CaseRow[]>(
    `/rest/v1/cases?select=id,user_id,created_at,updated_at,deleted_at,data&user_id=eq.${encodeURIComponent(session.user.id)}`,
    session,
    {
      headers: {
        Range: "0-9999"
      }
    }
  );

export const syncCasesWithSupabase = async (
  localCases: RealEstateCase[],
  initialSession: SupabaseSession
): Promise<CaseSyncResult> => {
  let session = initialSession;
  const pendingDeletions = loadPendingCaseDeletions();
  const pendingWipe = loadPendingCaseWipe();
  let cloudResult = await fetchCloudRows(session);
  session = cloudResult.session;

  if (pendingDeletions.length > 0 || pendingWipe) {
    const initialRowsById = new Map(cloudResult.data.map((row) => [row.id, row]));
    const deletionsById = new Map(pendingDeletions.map((item) => [item.id, item]));

    if (pendingWipe) {
      cloudResult.data.forEach((row) => {
        const cloudCase = !row.deleted_at ? normalizeCloudCase(row) : null;
        const cloudVersion = row.deleted_at ?? cloudCase?.updatedAt ?? row.updated_at;
        if (timestamp(pendingWipe) >= timestamp(cloudVersion)) {
          const existingDeletion = deletionsById.get(row.id);
          if (!existingDeletion || timestamp(pendingWipe) > timestamp(existingDeletion.deletedAt)) {
            deletionsById.set(row.id, { id: row.id, deletedAt: pendingWipe });
          }
        }
      });
    }

    const tombstonesToUpload = [...deletionsById.values()].flatMap((item) => {
      const cloudRow = initialRowsById.get(item.id);
      const cloudCase = cloudRow && !cloudRow.deleted_at ? normalizeCloudCase(cloudRow) : null;
      const cloudVersion = cloudRow?.deleted_at ?? cloudCase?.updatedAt ?? cloudRow?.updated_at;

      if (cloudRow && timestamp(item.deletedAt) < timestamp(cloudVersion)) {
        return [];
      }

      return [{
        id: item.id,
        user_id: session.user.id,
        created_at: item.deletedAt,
        updated_at: item.deletedAt,
        deleted_at: item.deletedAt,
        data: {}
      }];
    });

    session = await upsertRows(tombstonesToUpload, session);
    clearPendingCaseDeletions(pendingDeletions.map((item) => item.id));
    if (pendingWipe) {
      clearPendingCaseWipe();
    }

    if (tombstonesToUpload.length > 0) {
      cloudResult = await fetchCloudRows(session);
      session = cloudResult.session;
    }
  }

  const cloudRowsById = new Map(cloudResult.data.map((row) => [row.id, row]));
  const mergedById = new Map(localCases.map((caseItem) => [caseItem.id, caseItem]));
  const cloudOnlyOrder: string[] = [];

  cloudResult.data.forEach((row) => {
    const localCase = mergedById.get(row.id);

    if (row.deleted_at) {
      if (!localCase || timestamp(row.deleted_at) >= timestamp(localCase.updatedAt)) {
        mergedById.delete(row.id);
      }
      return;
    }

    const cloudCase = normalizeCloudCase(row);
    if (!cloudCase) {
      return;
    }

    if (!localCase) {
      mergedById.set(row.id, cloudCase);
      cloudOnlyOrder.push(row.id);
      return;
    }

    if (timestamp(cloudCase.updatedAt) > timestamp(localCase.updatedAt)) {
      mergedById.set(row.id, cloudCase);
    }
  });

  const mergedCases = [
    ...localCases.flatMap((caseItem) => {
      const mergedCase = mergedById.get(caseItem.id);
      return mergedCase ? [mergedCase] : [];
    }),
    ...cloudOnlyOrder.flatMap((caseId) => {
      const mergedCase = mergedById.get(caseId);
      return mergedCase ? [mergedCase] : [];
    })
  ];

  const rowsToUpload = mergedCases.flatMap((caseItem) => {
    const cloudRow = cloudRowsById.get(caseItem.id);
    const cloudCase = cloudRow && !cloudRow.deleted_at ? normalizeCloudCase(cloudRow) : null;
    const cloudVersion = cloudRow?.deleted_at ?? cloudCase?.updatedAt ?? cloudRow?.updated_at;

    if (cloudRow && timestamp(caseItem.updatedAt) <= timestamp(cloudVersion)) {
      return [];
    }

    return [{
      id: caseItem.id,
      user_id: session.user.id,
      created_at: caseItem.createdAt,
      updated_at: caseItem.updatedAt,
      deleted_at: null,
      data: caseItem
    }];
  });

  session = await upsertRows(rowsToUpload, session);
  return { cases: mergedCases, session };
};

export const caseCollectionsMatch = (first: RealEstateCase[], second: RealEstateCase[]) =>
  JSON.stringify(first) === JSON.stringify(second);
